import React from 'react';
import { Alert, Box, Container, Header, SpaceBetween } from '@cloudscape-design/components';

/**
 * ARR reconciliation ladder — renders the backend's `arr_reconciliation`
 * rows (P449272857) as an aligned label→amount table so the three big
 * figures (Fund Request ARR, current calculator total, Qualification ARR)
 * visibly add up instead of looking contradictory. Each row is
 * { label, amount, kind }; `kind` drives styling (anchor / total /
 * deduction / result). Deductions are pre-negated by the backend.
 */
const formatSignedCurrency = (amount) => {
  const abs = Math.abs(amount || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `−${abs}` : abs; // U+2212 minus sign for negatives
};

const ArrReconciliation = ({ rows, showLabel = true }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }
  return (
    <Box>
      {showLabel && (
        <Box variant="awsui-key-label">ARR reconciliation (current AWS pricing)</Box>
      )}
      <div style={{ fontFamily: 'monospace', marginTop: '4px' }}>
        {rows.map((r, i) => {
          const isResult = r.kind === 'result';
          const isDeduction = r.kind === 'deduction';
          const row = (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                paddingLeft: isDeduction ? '16px' : '0',
                // Neutral semi-transparent gray divider reads correctly in both
                // light and dark themes (a solid theme hex like #879596 would not
                // adapt; @cloudscape-design/design-tokens is not a dependency of
                // this package, confirmed by build failure).
                borderTop: isResult ? '1px solid rgba(128, 128, 128, 0.4)' : 'none',
                marginTop: isResult ? '4px' : '0',
                paddingTop: isResult ? '4px' : '0',
                fontWeight: isResult ? 'bold' : 'normal',
              }}
            >
              <span>{r.label}</span>
              <span>{formatSignedCurrency(r.amount)}</span>
            </div>
          );
          return (
            <Box
              key={`${r.label}-${i}`}
              color={isDeduction ? 'text-body-secondary' : undefined}
            >
              {row}
            </Box>
          );
        })}
      </div>
    </Box>
  );
};

/**
 * Pricing Calculator Warnings — structured, bulleted advisory section
 * (ticket P472176838, Change 3).
 *
 * Renders the backend's `pricing_calculator.warnings` array — each entry is
 * `{ type, message }` — as its own bullet under a single amber warning banner,
 * replacing the old single concatenated `| Note: ... | NEEDS_REVIEW: ...` line.
 * The section is extensible: any future advisory (tier downgrade, Fargate, ...)
 * the backend appends to `warnings[]` renders here with no frontend change.
 *
 * Legacy fallback (graceful, no data migration): before this ticket the
 * Bedrock>50% signal was surfaced as `status === 'interim_policy_review'` with
 * the warning folded into `reason` (rendered by ValidationSummaryBanner). For a
 * persisted FR that still carries that shape (no `warnings` array), render
 * nothing here — the legacy `reason` line still shows via the banner, so the
 * reviewer isn't left without the signal.
 *
 * @param {{ warnings?: Array<{type?: string, message: string}> }} pricingData
 */
const PricingCalculatorWarnings = ({ pricingData }) => {
  const warnings = Array.isArray(pricingData?.warnings) ? pricingData.warnings : [];
  const reconciliation = Array.isArray(pricingData?.arr_reconciliation)
    ? pricingData.arr_reconciliation
    : [];

  // Nothing to show → render nothing. Legacy interim_policy_review records
  // (no warnings, no ladder) fall through to the banner's `reason` line.
  if (warnings.length === 0 && reconciliation.length === 0) {
    return null;
  }

  // No warnings, ladder only → show the reconciliation in a NEUTRAL container.
  // Wrapping a clean/validated calculator's ladder in the amber warning Alert
  // would wrongly signal a problem where there is none.
  if (warnings.length === 0) {
    return (
      <Container header={<Header variant="h3">ARR reconciliation (current AWS pricing)</Header>}>
        <ArrReconciliation rows={reconciliation} showLabel={false} />
      </Container>
    );
  }

  return (
    <Alert type="warning" header="Pricing Calculator Warnings">
      <SpaceBetween size="s">
        <ArrReconciliation rows={reconciliation} />
        <SpaceBetween size="xxs">
          {warnings.map((w, i) => (
            <Box key={w?.type ? `${w.type}-${i}` : i} variant="p">
              {'• '}{w?.message}
            </Box>
          ))}
        </SpaceBetween>
      </SpaceBetween>
    </Alert>
  );
};

export default PricingCalculatorWarnings;
